import { Component, NgZone } from '@angular/core';
import { IonApp, IonRouterOutlet, ToastController, Platform, AlertController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StorageService } from './storage.service';
import { ApiService } from './api.service';
import { Browser } from '@capacitor/browser';
import { throwError, of, tap, Observable, filter, lastValueFrom, delay, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FirebaseMessagingService } from './app.firebase-messaging.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationActionPerformedEvent } from '@capacitor-firebase/messaging';
import { SplashScreen } from '@capacitor/splash-screen';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';

export const LANG_KEY = "_lang";
 
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet, TranslateModule],
})
export class AppComponent {

  constructor(
    private router: Router,
    private zone: NgZone,
    private translate: TranslateService,
    private storage: StorageService,
    private api: ApiService,
    private plaf: Platform,
    private toastCtl: ToastController,
    private pushNotis: FirebaseMessagingService,
    private swUpdate: SwUpdate,
    private alertCtl: AlertController,
  ) {
    this.translate.addLangs(['ca', 'es', 'en']);
    // obté lang desat a preferències o el browserLang per defecte
    this.storage.ready.then(()=>this.storage.get(LANG_KEY)).then(storedlang=>{
      const dflt = storedlang || this.translate.getBrowserLang();
      if (dflt && this.translate.getLangs().includes(dflt)) {
        this.translate.use(dflt)
      }
    });

    this.pushNotis.registerAppNotiActionHandler((event: NotificationActionPerformedEvent)=>{
      this.debug(event, 'Acció en una Noti');
      ()=>SplashScreen.hide();
      const { actionId, notification } = event;
      if (actionId == 'tap') {
        const { data } = notification;
        const { contact_id, destination } = data as {[k:string]:string};
        this.router.navigateByUrl(destination).finally(
          ()=>SplashScreen.hide()
        );
      }
    });
    this.pushNotis.liveNotis$.pipe(takeUntilDestroyed()).subscribe({
      next:([lastNoti])=>{
        if (!lastNoti) return;
        this.toastCtl.create({
          header: lastNoti.notification.title,
          message: lastNoti.notification.body,
          position:"middle",
          layout:'stacked',
          duration: 20000,
          color:'tertiary',
          cssClass:'noti-toast',
          buttons:['Ver', {text:"Cancelar", role:'cancel'}]
        }).then(toast=>{
          toast.present();
          return toast.onDidDismiss();
        }).then(({data, role})=>{
          if (!role) {
            this.router.navigateByUrl(lastNoti.data.destination);
          }
        })
      }
    });

    // Espera a tenir un l'usuari per iniciar el firebase
    // Nota: Potser es podria iniciar en un altre punt, p.ex: al dashboard
    this.api.session$.pipe(takeUntilDestroyed()).subscribe({
      next: (sessionUp:boolean)=>{
        if (sessionUp) { // hi ha sessió d'usuari
          this.pushNotis.initializeFirebase().then(token=>{
            this.debug(token, '[firebase token]');
            return lastValueFrom(
              this.api.postFBToken(token).pipe(
                catchError(err=>of(this.pushNotis.token))
              )
            )
          }).catch(err=>this.debug('firebase initialization', err));
        } else {
          if (this.pushNotis.token){ 
            this.pushNotis.deleteToken();
            this.api.deleteFBToken().subscribe();
          }
        }
      }
    });

    // només cal actualitzar les webApps
    if (!Capacitor.isNativePlatform()) {
      this.swUpdate.versionUpdates.pipe(
        takeUntilDestroyed(),
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      ).subscribe((evt) => {
        this.alertCtl.create({
          header:this.translate.instant('update.header'),
          message:this.translate.instant('update.msg'),
          buttons:[
            this.translate.instant('update.refresh'), 
            {text:this.translate.instant('update.cancel'), role:'cancel'}
          ]
        }).then(alert=>{
          alert.present();
          return alert.onDidDismiss();
        }).then(({role})=>{
          if (!role) document.location.reload();
        }).catch(err=>console.log(`[UpdatePrompter]`, err));
      });
    }

    // escolta els deep-links
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = new URL(event.url);
        switch(url.pathname) {
          case '/callback':
            Browser.close();
            const code = url.searchParams.get('code');
            if (!code) {
              throwError(()=>new Error('Unauthorized')).pipe(this.api.uify()).subscribe();
            } else {
              this.api.remataLoginGoogle({ code }).pipe(this.api.uify()).subscribe({
                error: err=>this.debug(err, 'remataLoginGoogle ha fallat:'),
                complete: ()=>this.router.navigateByUrl('/select-company')
              })
            }
            break;
          default:
            // pel dashboard:
            // `/${company_id}/dashboard?doc=${company_protections_workers_deliveries_id}`
            // Nota: com que el callback del docusign (al dashboard) conté el company_id, 
            // no el puc comparar directament... 
            if (url.pathname.match(/dashboard/)) {
              // caldria que canvies algun paràmetre a la ruta per que es tornéssin a executar guards i resolvers
              // this.router.navigateByUrl(`${url.pathname}?${url.search}`, {
              //   onSameUrlNavigation: 'reload'
              // });

              const signed_delivery = url.searchParams.get('doc');
              const signed_event = url.searchParams.get('event');
              
              // un observable auxiliar s'encarrega de reaccionar-hi
              const signature_post$ = (!signed_delivery||signed_event!='signing_complete')?
                of(null) :
                this.api.ipeDeliverySigned(+signed_delivery).pipe(
                  tap(_=>this.toastCtl.create({
                    message: translate.instant('dashboard.signature_ok'),
                    buttons: [ translate.instant('revisions.entesos') ],
                    duration: 4500,
                    position: 'middle'
                  }).then(toast=>toast.present()))
                ) as Observable<any>;
              signature_post$.subscribe({
                next: (resp)=>{
                  if (!!resp) {
                    // avisa que s'ha cridat el callback i cal refrescar l'estatus
                    this.api.signIPEDeliveryCallbackCalled.next();
                  }
                }
              })
            } else if (url.pathname.match(/consentiment-done/)) {
              const s_event = url.searchParams.get('event');
              this.debug(s_event, 'docusign event');
              this.router.navigateByUrl(url.pathname);
            }
            // If no match, do nothing - let regular routing
            // logic take over
            this.debug(event, 'Opening deep-link');
        }
      });
    });
  }

  debug(sth:any, tag="") {
    if (environment.production) return;
    console.log(`DEBUG ${this.constructor.name} ${tag}`, sth);
  }
}
